import { useNavigate, useLocation, Link } from "react-router-dom";
import { Search, QrCode, Bell, Plus, ChevronRight, User as UserIcon, Settings, LogOut } from "lucide-react";
import { useState, useMemo, useSyncExternalStore } from "react";
import logo from "@/assets/logo.png";
import { SearchDialog } from "@/components/SearchDialog";
import { SearchInline } from "@/components/SearchInline";
import { useIsMobile } from "@/hooks/use-mobile";
import { getBreadcrumbLabel, getBreadcrumbLabelsVersion, subscribeBreadcrumbLabels } from "@/lib/breadcrumb-labels";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROUTE_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  bookings: "Bokningar",
  tires: "Däck",
  customers: "Kunder",
  storage: "Lager",
  reports: "Rapporter",
  settings: "Inställningar",
  companies: "Företag",
  admin: "Administration",
  scanner: "Skanner",
  "booking-settings": "Bokningsinställningar",
};

interface AppHeaderProps {
  hideSearch?: boolean;
}

export function AppHeader({ hideSearch = false }: AppHeaderProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, signOut } = useAuth();

  // Subscribe to dynamic label registry so breadcrumbs re-render when
  // pages register a friendly name for a UUID/slug segment.
  const labelsVersion = useSyncExternalStore(
    subscribeBreadcrumbLabels,
    getBreadcrumbLabelsVersion,
    () => 0,
  );

  const crumbs = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    return parts.map((p, i) => {
      const friendly = getBreadcrumbLabel(p);
      // For dynamic id segments (long/UUID-like), prefer friendly label and
      // fall back to a short ellipsis instead of showing the raw UUID.
      const looksLikeId = p.length >= 16 && /[0-9a-f-]{16,}/i.test(p);
      const label = ROUTE_LABELS[p] || friendly || (looksLikeId ? "…" : p);
      return { label, path: "/" + parts.slice(0, i + 1).join("/") };
    });
    // labelsVersion is intentionally part of the deps so registry updates
    // (e.g. StorageDetail registering its location code) rebuild the trail.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, labelsVersion]);

  return (
    <>
      <header className="sticky top-0 z-50 flex h-14 w-full max-w-full items-center gap-2 md:gap-3 overflow-hidden border-b border-border bg-card/85 backdrop-blur-xl px-3 md:px-5">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0 focusable rounded-lg"
        >
          <img src={logo} alt="TireVault" className="h-9 md:h-10 w-auto object-contain" />
        </button>

        {/* Breadcrumb */}
        {!isMobile && (
          <nav className="hidden md:flex items-center gap-1 text-xs text-foreground-muted ml-2">
            <span className="text-foreground-subtle">TireVault</span>
            {crumbs.map((c, i) => (
              <span key={c.path} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-foreground-subtle" />
                <Link
                  to={c.path}
                  className={
                    i === crumbs.length - 1
                      ? "text-foreground font-semibold"
                      : "hover:text-foreground transition-colors"
                  }
                >
                  {c.label}
                </Link>
              </span>
            ))}
          </nav>
        )}

        {/* Search + scan */}
        {!hideSearch && (
        <div className="flex-1 min-w-0 max-w-xl mx-auto flex items-center gap-1.5">
          {isMobile ? (
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Sök"
              className="flex items-center gap-2 flex-1 min-w-0 h-9 px-3 rounded-lg bg-background-2 hover:bg-accent-soft border border-border text-foreground-muted hover:text-accent-ink transition-colors text-sm focusable"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="truncate">Sök kunder, däck, fordon…</span>
            </button>
          ) : (
            <SearchInline />
          )}
          <button
            onClick={() => navigate("/scanner")}
            aria-label="QR-skanner"
            className="shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-background-2 hover:bg-accent-soft border border-border text-foreground-muted hover:text-accent-ink transition-colors focusable"
          >
            <QrCode className="h-[18px] w-[18px]" />
          </button>
        </div>
        )}
        {hideSearch && <div className="flex-1" />}

        {/* Minimal profile menu — always visible when full nav is hidden */}
        {hideSearch && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="Profil"
                className="shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-background-2 hover:bg-accent-soft border border-border text-foreground-muted hover:text-accent-ink transition-colors focusable"
              >
                <UserIcon className="h-[18px] w-[18px]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <Settings className="mr-2 h-4 w-4" /> Profilinställningar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => signOut()}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" /> Logga ut
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Right cluster */}
        {!hideSearch && (
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <button
            aria-label="Notiser"
            className="relative flex items-center justify-center h-9 w-9 rounded-lg bg-background-2 hover:bg-accent-soft border border-border text-foreground-muted hover:text-accent-ink transition-colors focusable"
          >
            <Bell className="h-[18px] w-[18px]" />
          </button>
          <button
            onClick={() => navigate("/tires?new=1")}
            className="hidden lg:inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110 transition-all focusable shadow-soft"
          >
            <Plus className="h-4 w-4" />
            Ny däckuppsättning
          </button>
        </div>
        )}
      </header>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
